<template>
  <div class="input-datetime">
    <div class="input-datetime__input_group">
      <div class="input-datetime__field">
        <InputText
          v-if="loading"
          loading
          :style="{
            width: '100%',
          }"
        />
        <InputDate
          v-else
          :model-value="modelValue"
          :disabled="disabled || loading"
          :min-date="min"
          :max-date="max"
          :style="{
            width: '100%',
          }"
          :invalid="invalid"
          :prm-calendar-format="prmCalendarFormat"
          @update:model-value="onUpdateDate"
        />
      </div>
      <div class="input-datetime__field">
        <InputTime
          :model-value="modelValueTime"
          :disabled="disabled"
          :class="{
            'p-invalid': invalid,
          }"
          :style="{
            width: '100%',
          }"
          :loading="loading"
          :min="minTimeMilliseconds"
          :max="maxTimeMilliseconds"
          :show-seconds="showSeconds"
          :invalid="invalid || !!errorMessage"
          @update:model-value="onUpdateTime"
        />
      </div>
    </div>
    <span
      v-if="!!errorMessage"
      class="input-wrapper-error"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import {
  ELanguage,
} from '@qmulus/frontend-shared/type/locale.ts';
import {
  getPrimevueCalendarFormat,
} from '@qmulus/frontend-shared/util/date-format.ts';
import {
  computed,
} from 'vue';

import dayjs from '../../plugins/dayjs.ts';
import {
  compareDatesUTC,
  getMillisecondsSinceStartOfDay,
} from '../../utils/date';
import InputDate from '../input-date/input-date.vue';
import InputText from '../input-text/input-text.vue';
import InputTime from '../input-time/input-time.vue';

const props = defineProps<{
  modelValue: Date | undefined,
  locale: ELanguage,
  disabled?: boolean,
  loading?: boolean,
  invalid?: boolean,
  min?: Date,
  max?: Date,
  showSeconds?: boolean,
  errorMessage?: string,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date): void,
}>();

const prmCalendarFormat = computed(() => getPrimevueCalendarFormat(props.locale));

const modelValueTime = computed<number | undefined>(() => getMillisecondsSinceStartOfDay(props.modelValue));
const minTimeMilliseconds = computed<number | undefined>(() => {
  // We only care about the time limit if the modelValue is not bigger as min
  if (props.modelValue && props.min && compareDatesUTC(props.modelValue, props.min) !== 1) {
    return getMillisecondsSinceStartOfDay(props.min);
  }
  return 0;
});
const maxTimeMilliseconds = computed<number | undefined>(() => {
  // We only care about the time limit if the modelValue is not smaller as max
  if (props.modelValue && props.max && compareDatesUTC(props.modelValue, props.max) !== -1) {
    return getMillisecondsSinceStartOfDay(props.max);
  }
  return 0;
});

function onUpdateDate(date: Date | Date[] | (Date | null)[] | null | undefined) {
  if (date && !Array.isArray(date)) {
    const newDate = new Date(date);
    const duration = dayjs.duration(modelValueTime.value || 0);
    newDate.setHours(duration.hours(), duration.minutes(), duration.seconds(), duration.milliseconds());
    emit('update:modelValue', newDate);
  }
}

function onUpdateTime(milliseconds: number) {
  const date = props.modelValue ? new Date(props.modelValue) : new Date();
  const duration = dayjs.duration(milliseconds);
  date.setHours(duration.hours(), duration.minutes(), duration.seconds(), duration.milliseconds());
  emit('update:modelValue', date);
}
</script>

<style scoped lang="scss">
.input-datetime {
  display: flex;
  flex-direction: column;
  gap: var(--gap);

  &__input_group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--gap-union);
    align-items: flex-end;
  }

  &__field {
    width: 100%
  }
}
</style>
